/**
 * The collection cover picker, extracted from `CollectionsScreen` unchanged so the table row and
 * the detail panel open the SAME picker rather than two that drift.
 *
 * Options come from `admin_search_guide_cover_options`, which is backed by the SAME
 * `search_guide_venue_ids` the rail itself uses — a cover the rail cannot render is never offered,
 * and there is no second definition of "what is in this collection".
 *
 * ⚠️ z-[60], not z-50: this can open ON TOP of the detail panel (z-50). Behind it, the scrim would
 * swallow the clicks and the grid would look dead.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useEscToClose } from '../lib/useEscToClose';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonRows } from '../ui/Skeleton';
import {
  GLOBAL_KINDS,
  errText,
  rpcMissing,
  type CoverOption,
  type GuideRow,
} from '../lib/collectionKinds';

export function CollectionCoverPickerModal({
  open,
  guide,
  previewCityId,
  previewCityName,
  onClose,
  onPicked,
}: {
  open: boolean;
  guide: GuideRow | null;
  previewCityId: string;
  previewCityName: string | null;
  onClose: () => void;
  onPicked: (guideId: string, venueId: string | null, option: CoverOption | null) => void;
}) {
  const [options, setOptions] = useState<CoverOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEscToClose(open && !busy, onClose);

  const guideId = guide?.id ?? null;

  useEffect(() => {
    if (!open || !guideId) {
      setOptions([]);
      setSetupNote(null);
      setLoadError(null);
      setSelectedId(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setSetupNote(null);
    setLoadError(null);
    setSelectedId(guide?.cover_venue_id ?? null);
    void (async () => {
      const { data, error } = await supabase.rpc('admin_search_guide_cover_options', {
        p_guide_id: guideId,
        p_city_id: previewCityId || null,
        p_limit: 60,
      });
      if (cancelled) return;
      if (error) {
        if (rpcMissing(error.message, 'admin_search_guide_cover_options')) {
          setSetupNote(
            'Choosing a cover needs one migration that has not been applied yet: ' +
              'supabase/migrations/20260871000000_collection_admin_covers.sql. Apply it, then reload — ' +
              'everything else on this page already works.',
          );
        } else {
          setLoadError(error.message);
        }
        setOptions([]);
      } else {
        setOptions(Array.isArray(data) ? (data as CoverOption[]) : []);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [guide?.cover_venue_id, guideId, open, previewCityId]);

  const setCover = useCallback(
    async (venueId: string | null) => {
      if (!guideId) return;
      setBusy(true);
      setLoadError(null);
      try {
        const { error } = await supabase.rpc('admin_set_search_guide_cover', {
          p_id: guideId,
          p_cover_venue_id: venueId,
          p_city_id: previewCityId || null,
        });
        if (error) {
          if (rpcMissing(error.message, 'admin_set_search_guide_cover')) {
            throw new Error(
              'Not set up yet — apply supabase/migrations/20260871000000_collection_admin_covers.sql.',
            );
          }
          throw error;
        }
        onPicked(guideId, venueId, options.find((o) => o.venue_id === venueId) ?? null);
      } catch (e: unknown) {
        setLoadError(errText(e, 'Could not set the cover'));
      } finally {
        setBusy(false);
      }
    },
    [guideId, onPicked, options, previewCityId],
  );

  if (!open || !guide) return null;

  const cityScoped = !GLOBAL_KINDS.has(guide.kind) && !guide.city_id;
  const selected = options.find((o) => o.venue_id === selectedId) ?? null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-label="Choose a collection cover"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-3xl rounded-xl border border-border bg-surface"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div className="min-w-0">
            <h2 className="admin-label-primary text-base">Cover — {guide.title}</h2>
            <p className="mt-1 text-xs text-mutedDim">
              {cityScoped ? (
                <>
                  This collection follows the reader&apos;s city, so it is a different list
                  everywhere. Pinning a cover here fixes the card{' '}
                  <span className="text-muted">in {previewCityName ?? 'the preview city'} only</span> —
                  every other city keeps the automatic pick.
                </>
              ) : (
                <>One list worldwide, so a pinned cover applies to everybody.</>
              )}
            </p>
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted transition-colors hover:bg-white/[0.04] hover:text-white disabled:opacity-50"
          >
            Close
          </button>
        </div>

        <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-4">
          {loadError ? <ErrorBanner>{loadError}</ErrorBanner> : null}
          {setupNote ? (
            <div className="rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
              {setupNote}
            </div>
          ) : null}

          <section>
            <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
              Showing now
            </h3>
            <div className="flex items-center gap-4">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg border border-border bg-black">
                {guide.effective_cover_photo_url ? (
                  <img
                    src={guide.effective_cover_photo_url}
                    alt=""
                    referrerPolicy="no-referrer"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-[11px] text-mutedDim">
                    none
                  </div>
                )}
              </div>
              <div className="min-w-0 text-xs leading-relaxed text-muted">
                <div className="truncate text-textPrimary">
                  {guide.effective_cover_venue_name ?? '—'}
                </div>
                <div className="mt-0.5 text-mutedDim">
                  {guide.cover_venue_id
                    ? guide.cover_in_scope
                      ? 'Pinned by hand.'
                      : 'Pinned, but not in this city — the automatic pick is what renders here.'
                    : 'Automatic: the first venue whose photo no other collection is already using.'}
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="mb-2 text-[10px] font-medium uppercase tracking-[0.14em] text-mutedDim">
              Venues in this collection
            </h3>
            {loading ? (
              <SkeletonRows rows={4} />
            ) : options.length === 0 ? (
              <EmptyState
                title="Nothing to choose from"
                hint={
                  setupNote
                    ? 'Apply the migration above, then reload.'
                    : cityScoped
                      ? 'This collection is empty in the preview city. Pick a city with more rated venues at the top of the page.'
                      : 'This collection has no venues with photos right now.'
                }
              />
            ) : (
              <ul className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {options.map((o) => {
                  const active = o.venue_id === selectedId;
                  return (
                    <li key={o.venue_id}>
                      <button
                        type="button"
                        disabled={busy || !o.photo_url}
                        aria-pressed={active}
                        onClick={() => setSelectedId(o.venue_id)}
                        className={`block w-full overflow-hidden rounded-lg border text-left transition-colors disabled:opacity-50 ${
                          active ? 'border-accent ring-2 ring-accent' : 'border-border hover:border-white/30'
                        }`}
                      >
                        <div className="aspect-square w-full bg-black">
                          {o.photo_url ? (
                            <img
                              src={o.photo_url}
                              alt=""
                              referrerPolicy="no-referrer"
                              loading="lazy"
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center text-[10px] text-mutedDim">
                              no photo
                            </div>
                          )}
                        </div>
                        <div className="bg-rowAlt px-2 py-1.5">
                          <div className="truncate text-[11px] text-muted">
                            {o.name?.trim() || 'Untitled venue'}
                          </div>
                          {/* Repo rule: a DISPLAYED count is distinct raters (venue_shado_unique_users). */}
                          <div className="mt-0.5 text-[10px] tabular-nums text-mutedDim">
                            {o.score ?? '—'} · {o.rater_count} rater{o.rater_count === 1 ? '' : 's'}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border px-5 py-4">
          <span className="mr-auto truncate text-[11px] text-mutedDim">
            {selected ? `Selected: ${selected.name?.trim() || 'Untitled venue'}` : 'Select a venue'}
          </span>
          <button
            type="button"
            disabled={busy || !guide.cover_venue_id}
            onClick={() => void setCover(null)}
            className="admin-btn admin-btn-secondary admin-btn-sm"
            title="Return this collection to the automatic, de-duplicated cover"
          >
            Use automatic
          </button>
          <button
            type="button"
            disabled={busy || !selectedId || selectedId === guide.cover_venue_id}
            onClick={() => void setCover(selectedId)}
            className="admin-btn admin-btn-primary admin-btn-sm"
          >
            {busy ? 'Saving…' : 'Set as cover'}
          </button>
        </div>
      </div>
    </div>
  );
}
